import React from 'react';
import { Tooltip } from 'reactstrap';
import PropTypes from 'prop-types';

const propTypes = {
    autoComplete: PropTypes.string,  // String value to override browser autocomplete
    autoFocus: PropTypes.bool,       // Focus on input on load
    className: PropTypes.string,     // Custom class name to add to input container
    disabled: PropTypes.bool,        // Disable input
    id: PropTypes.string,            // Input id
    inputBtnClick: PropTypes.func,   // Input icon button function
    inputBtnIcon: PropTypes.string,  // Input icon button icon class name
    inputBtnLabel: PropTypes.string, // Input icon button aria label
    inputErrorMsg: PropTypes.string, // Input inline error message
    maxLength: PropTypes.number,     // Max input value length
    name: PropTypes.string,          // Input name
    onBlur: PropTypes.func,          // Function triggered on blur
    onChange: PropTypes.func,          // Function triggered on onChange
    onFocus: PropTypes.func,         // Function triggered on focus    
    onKeyDown: PropTypes.func,         // Function triggered on onKeyDown
    onKeyPress: PropTypes.func,         // Function triggered on onKeyPress
    onKeyUp: PropTypes.func,         // Function triggered on onKeyUp
    pattern: PropTypes.string,       // Regular Expression Pattern
    placeholder: PropTypes.string,   // Input placeholder text
    required: PropTypes.bool,        // Set to true so input empty validation check will trigger on blur and keypress
    showAsInvalid: PropTypes.bool,   // Override component validation and display input as invalid
    showClearBtn: PropTypes.bool,    // Display clear input icon button
    showEye: PropTypes.bool,         // Display password entry visibility toggle
    tooltipID: PropTypes.string,     // Tooltip id which is required to display tooltip message
    tooltipMsg: PropTypes.string,    // Tooltip message
    type: PropTypes.string,          // Input type
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) // Input value
};

// Magic strings
const ShowPassword = "Show Password";
const HidePassword = "Hide Password";
const text = "text";
const password = "password";
const span = "SPAN";

export default class GenericInput extends React.Component {

    constructor(props) {
        super(props);
        this.toggleTooltip = this.toggleTooltip.bind(this);
        this.validateOnBlur = this.validateOnBlur.bind(this);
        this.validateOnMount = this.validateOnMount.bind(this);
        this.onChange = this.onChange.bind(this);
        this.clearInput = this.clearInput.bind(this);
        this.onEyeClick = this.onEyeClick.bind(this);
        this.togglePasswordField = this.togglePasswordField.bind(this);
        this.setClearBtn = this.setClearBtn.bind(this);

        this.state = {
            tooltipOpen: false,
            invalidInput: false,
            touched: false,
            value: this.props.value,
            enableClearBtn: false
        };
        this.genericInputEl = React.createRef();
        this.parentOfInput = React.createRef();
    }

    componentDidMount() {
        this._isMounted = true;
        this.validateOnMount();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    toggleTooltip() {
        this.setState({
            tooltipOpen: !this.state.tooltipOpen
        });
    }

    onChange(event) {
        const { onChange, required } = this.props;
        const value = event.target.value;
        if (onChange) {
            onChange(event, value, required && value.trim() !== '');
        }
        this.setState({ value: value });
    }

    togglePasswordField(e) {
        if (e.target && e.target.classList.contains("password-eye")) return;
        if (this._isMounted) {
            const input = this.genericInputEl.current;            
            input.type = password;
            const passwordEyeSpan = this.parentOfInput.current.querySelector("button.password-eye");            
            this.togglePasswordEye(passwordEyeSpan, input.type);          
        }
    }

    isValid(value) {
        const { pattern } = this.props;
        return Boolean(value) || (value && value.trim()) && (typeof pattern === 'undefined' || pattern.test(value));
    }

    validateOnBlur(event) {
        const value = event.target.value;
        const isValid = this.isValid(value);
        if (this.props.onChange) {
            this.props.onChange(event, value, isValid);
        }
        if (this.props.required || this.props.pattern) {
            event.persist();
            this.setState({ invalidInput: !isValid });
        }
        this.setState({
            touched: true
        });
    }

    validateOnMount() {
        const value = this.state.value;
        const isValid = this.isValid(value);
        if (this.props.required || this.props.pattern) {
            this.setState({ invalidInput: !isValid });
        }
    }

    clearInput(event) {
        if (this._isMounted) {
            const input = this.genericInputEl.current;
            input.value = "";
            var e = { event, target: { value: "", name: this.props.name ? this.props.name : "" } };
            this.onChange(e);
            input.focus();
        }
    }

    setClearBtn(value) {
        setTimeout(() => { this._isMounted && this.setState({ enableClearBtn: value }) }, 300);
    }

    togglePasswordEye(button, inputType) {
        if (!button) return;
        const { type, showEye } = this.props;
        if (type !== password && !showEye) return;
        if (inputType === password) {         
            button.title = ShowPassword;
            button.offsetParent.classList.remove("active");
            button.style.display = "";
        }
        else {            
            button.title = HidePassword;
            button.offsetParent.classList.add("active");
            button.style.display = "";
        }
    }

    onEyeClick(e) {
        const { type } = this.props;
        if (type !== password) return;
        if (this._isMounted) {
            const input = this.genericInputEl.current;
            if (input.type === password) {
                input.type = text;
            } else {
                input.type = password;
            }          
            if (e.target)
                this.togglePasswordEye(e.target, input.type);
            input.focus();
        }
    }   

    onKeyUp = e => {
        if (!this.props.pattern && this.state.touched) {
            this.validateOnBlur(e);
            if (this.props.onKeyUp) {
                this.props.onKeyUp(e);
            }
        }
    }

    onKeyDown = e => {
        if (!this.props.pattern || e.altKey || e.ctrlKey) {
            return;
        }
        else if (!this.props.pattern.test(e.key)) {
            e.preventDefault();
            return;
        }

        this.validateOnBlur(e);
        if (this.props.onKeyDown) {
            this.props.onKeyDown(e);
        }
    }

    onKeyPress = e => {
        if (this.props.onKeyPress) {
            this.props.onKeyPress(e);
        }
    }

    onPaste = event => {
        if (this.props.pattern) {
            const data = window.clipboardData ? window.clipboardData.getData('text') : event.clipboardData.getData('text/plain');
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            const value = target.value;
            const maxLength = this.props.maxLength ? Math.min(this.props.maxLength - value.length + end - start, data.length) : data.length;
            let filteredData = '';
            for (let i = 0, count = 0; i < data.length && count < maxLength; i++) {
                if (this.props.pattern.test(data[i])) {
                    filteredData += data[i];
                    count++;
                }
            }
            target.value = value.substr(0, start) + filteredData + value.substr(end);
            target.selectionStart = target.selectionEnd = start + filteredData.length;
            event.preventDefault();
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
            const event2 = document.createEvent('Event'); //new Event('input', { bubbles: true });
            event2.initEvent('init', true, true);
            nativeInputValueSetter.call(target, target.value);
            target.dispatchEvent(event2);
            if (this.props.onChange) {
                const isValid = this.isValid(target.value);
                this.props.onChange(event, isValid);
            }
        }
    }

    onParentOfInputBlur = e => {
        const { type, showEye, showClearBtn } = this.props;
        if (type === password && showEye) { this.togglePasswordField(e) }
        if (showClearBtn) { this.setClearBtn(false) }
    }

    render() {
        const {
            placeholder,
            value,
            required,
            name,
            id,
            autoFocus,
            onBlur,
            disabled,
            maxLength,
            inputErrorMsg,
            tooltipID,
            tooltipMsg,
            type,
            className,
            autoComplete,
            showAsInvalid,
            onFocus,
            inputBtnClick,
            inputBtnIcon,
            inputBtnLabel,
            showClearBtn,
            showEye
        } = this.props;

        const thisValue = value || this.state.value || ""; // in case the value property is being applied externally
        const containerClassName =
            (thisValue ? " input-not-empty" : " input-empty") + (className ? " " + className : "") +
            (this.state.touched ? " input-touched" : " input-untouched") +
            (this.state.invalidInput || showAsInvalid ? " input-invalid" : " input-valid") +
            (showAsInvalid ? " input-show-invalid" : "") + 
            (((tooltipID && tooltipMsg) || (inputBtnClick && inputBtnIcon)) ? " input-append" : "") + (showClearBtn ? " input-clear-btn" : "") +
            (showEye && thisValue && type === password ? " input-password-btn" : "");

        return (<div className={"il-group" + containerClassName} ref={this.parentOfInput} onBlur={e => this.onParentOfInputBlur(e)}>
            <input type={type ? type : "text"}
                className="form-control"
                value={thisValue}
                required={required}
                name={name}
                id={id || name}
                disabled={disabled}
                autoFocus={autoFocus}
                placeholder={placeholder ? placeholder : ""}
                maxLength={maxLength ? maxLength : ""}
                onChange={e => this.onChange(e)}
                onBlur={onBlur ? onBlur : this.validateOnBlur}
                onKeyUp={this.onKeyUp}
                onKeyDown={this.onKeyDown}
                onKeyPress={this.onKeyPress}
                autoComplete={autoComplete}
                onPaste={this.onPaste}
                onFocus={(e) => {                   
                    if (onFocus) { onFocus(e); } this.setClearBtn(true);
                }}
                //aria-label={placeholder ? placeholder : ""}
                title={placeholder ? "Please fill out the " + placeholder : "Please fill out this field."}
                ref={this.genericInputEl}
            />
            {thisValue && showEye && type === password &&
                <div className="password-icon-append">
                    <button className="password-eye" type="button" aria-label={ShowPassword} title={ShowPassword} onClick={this.onEyeClick} onMouseDown={(e) => { e.preventDefault(); }}><span className="icon-eye" /></button>
                </div>
            }
            {showClearBtn && thisValue && this.state.enableClearBtn &&
                <div className="clear-btn-container">
                    <button className="clear-btn" type="button" aria-label="clear button" onClick={e => this.clearInput(e)}><span className="icon-input-clear" /></button>
                </div>
            }
            {(this.state.invalidInput || showAsInvalid) && 
                <div className="input-icon-append input-icon__error">
                <span className="icon-input-error"/>
                </div>
            }
            {(inputBtnClick && inputBtnIcon) &&
                <div className="input-icon-append">
                    <button type="button" onClick={e => inputBtnClick(e)} aria-label={inputBtnLabel ? inputBtnLabel : ""}><span className={inputBtnIcon} /></button>
                </div>
            }
            {(tooltipID && tooltipMsg) &&
                <div className="input-icon-append">
                    <span className="icon-info" id={tooltipID} />
                    <Tooltip placement="top" isOpen={this.state.tooltipOpen} autohide={false} target={tooltipID} toggle={this.toggleTooltip}>
                        {tooltipMsg}
                    </Tooltip>
                </div>
            }
            <div className="il-label" aria-hidden="true">{placeholder}</div>            
            {this.state.invalidInput || showAsInvalid ? <div className="input-error-msg" htmlFor={id || name} tabIndex="0">{inputErrorMsg ? inputErrorMsg : <>Enter a valid <span className="input-error-msg__placeholder">{placeholder}</span>.</>}</div> : null}
        </div>
        );
    }
}

GenericInput.propTypes = propTypes;